/**
 * Result-card data helpers for the search results page (P25009-445).
 *
 * Everything here is derived from what the search query already returned —
 * the loaded page of listings with their authors and stock. Nothing hits the
 * network. That means two of the sorts are PAGE-LOCAL by design:
 * "Lägsta totalpris monterat" and "Snabbast klart" cannot be expressed as
 * Marketplace API sorts (the montering price lives on the author profile, not
 * the listing), so they reorder the fetched page. mountedTotal at least
 * fetches cheapest-first from the server, which keeps the page it reorders
 * the right page to be looking at. The honest fix is a denormalised
 * `pub_totalPriceMounted` written at publish time — tracked on the ticket.
 */

import {
  resolveSalesUnit,
  pieceAmount,
  setAmount,
  getUnitNoun,
  hasSalesUnit,
} from '../../util/salesUnit';
import {
  MONTERING_TEMPLATE_KEY,
  autoServiceIds,
  servicesForListing,
  serviceSubunits,
} from '../../util/serviceTargetingCore';
import { checkWheelFitment } from '../../util/wheelFitment';

/** The seller's active montering service for THIS listing, or null: only where
 * the seller has montering set to show for its type and condition
 * (P25009-484 BR-3), so a tyre card never promises montering checkout would
 * not offer. */
export const monteringServiceFor = (author, publicData = {}) =>
  servicesForListing(author?.attributes?.profile?.publicData?.services, publicData).find(
    s => s.templateKey === MONTERING_TEMPLATE_KEY
  ) || null;

/** True when montering comes with the listing whatever the buyer picks: new
 * complete wheels and packages (P25009-484 BR-5). The card then says the
 * total is "inkl. montering" instead of offering it. */
export const monteringIncludedFor = listing =>
  autoServiceIds(listing?.author?.attributes?.profile?.publicData?.services, [
    { publicData: listing?.attributes?.publicData || {} },
  ]).length > 0;

/** Sharetribe currentStock quantity, or null when the listing doesn't track stock. */
export const stockQuantityOf = listing => {
  const quantity = listing?.currentStock?.attributes?.quantity;
  return typeof quantity === 'number' && Number.isFinite(quantity) ? quantity : null;
};

/**
 * Piece/set price split for a sellable listing, in currency subunits.
 * @returns {{piece: Number, set: Number, setSize: Number, salesUnit: String,
 *            currency: String, noun: {one: String, many: String}}|null}
 */
export const listingPricePieces = listing => {
  const { price, publicData } = listing?.attributes || {};
  if (!price?.amount || !hasSalesUnit(publicData || {})) return null;
  const resolved = resolveSalesUnit(publicData);
  return {
    piece: pieceAmount(price.amount, resolved),
    set: setAmount(price.amount, resolved),
    setSize: resolved.setSize,
    salesUnit: resolved.salesUnit,
    currency: price.currency,
    noun: getUnitNoun(publicData),
  };
};

/** Montering for the set the card prices (setSize items), in subunits, by the
 * service's own price unit (P25009-484 BR-7); null without montering. */
export const monteringSetAmount = listing => {
  const pieces = listingPricePieces(listing);
  if (!pieces) return null;
  const publicData = listing?.attributes?.publicData || {};
  const montering = monteringServiceFor(listing?.author, publicData);
  if (!montering) return null;
  const amount = serviceSubunits(montering, [{ publicData, physicalItems: pieces.setSize }]);
  return amount > 0 ? amount : null;
};

/** Set total + montering for that set, in subunits; null when the seller
 * doesn't offer montering on this listing or hasn't priced it. */
export const mountedTotalAmount = listing => {
  const montering = monteringSetAmount(listing);
  return montering === null ? null : listingPricePieces(listing).set + montering;
};

/* ---- distance ---- */

/* The search's radius chips use the same helpers (P25009-425), so they live
 * with the buyer's area in util/buyerArea.js. */
export { distanceKm, formatDistance } from '../../util/buyerArea';

/* ---- "Samma däck från N säljare" grouping (BR-6) ---- */

const groupKeyOf = listing => {
  const { title, publicData } = listing?.attributes || {};
  if (!title || !hasSalesUnit(publicData || {})) return null;
  const normTitle = String(title)
    .toLowerCase()
    .replace(/[^a-z0-9åäö]+/g, ' ')
    .trim();
  if (!normTitle) return null;
  const dims = [
    publicData?.tireWidthFront,
    publicData?.profileFront,
    publicData?.diameter,
  ]
    .map(v => (v === null || v === undefined ? '' : String(v)))
    .join('/');
  return `${publicData?.adType || publicData?.listingType || ''}|${normTitle}|${dims}`;
};

/**
 * Groups the loaded page by "the same product from another seller": same
 * normalized title + dimension, different authors. Page-local on purpose —
 * it can only undercount, never claim sellers that don't exist.
 *
 * @returns {Object} listing uuid -> {sellerCount, fromPiece, currency, noun}
 */
export const buildSameTyreGroups = (listings = []) => {
  const groups = new Map();
  listings.forEach(listing => {
    const key = groupKeyOf(listing);
    if (!key) return;
    const entry = groups.get(key) || { authors: new Set(), fromPiece: null, currency: null, noun: null, ids: [] };
    const authorId = listing?.author?.id?.uuid;
    if (authorId) entry.authors.add(authorId);
    const pieces = listingPricePieces(listing);
    if (pieces && (entry.fromPiece === null || pieces.piece < entry.fromPiece)) {
      entry.fromPiece = pieces.piece;
      entry.currency = pieces.currency;
    }
    if (pieces && !entry.noun) entry.noun = pieces.noun;
    entry.ids.push(listing.id.uuid);
    groups.set(key, entry);
  });

  const byListingId = {};
  groups.forEach(entry => {
    if (entry.authors.size < 2) return;
    entry.ids.forEach(id => {
      byListingId[id] = {
        sellerCount: entry.authors.size,
        fromPiece: entry.fromPiece,
        currency: entry.currency,
        noun: entry.noun,
      };
    });
  });
  return byListingId;
};

/* ---- fit first (P25009-486 BR-1) ---- */

/**
 * Where a listing ranks for the buyer's car: 0 = the fit check passes
 * ("✓ Passar"), 1 = it cannot tell, 2 = it fails ("Passar inte"). The same
 * check the card badge and the buy box run.
 */
export const fitBandOf = (listing, car) => {
  if (!car) return 1;
  const verdict = checkWheelFitment({
    listingPublicData: listing?.attributes?.publicData || {},
    licensePlateInfo: car,
  });
  return verdict?.compatible === true ? 0 : verdict?.compatible === false ? 2 : 1;
};

/* 12 months, the age after which a listing leaves the fit results (BR-3). */
export const STALE_AFTER_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * The last time the seller stood behind this price and stock: the
 * priceStockUpdatedAt stamp (wizard, import and supplier feed write it),
 * else when the listing was created. Never publicData.updateDate — a cron
 * rewrites that weekly to lift premium listings in the sort.
 */
export const freshnessMsOf = listing => {
  const stamp = listing?.attributes?.publicData?.priceStockUpdatedAt;
  const stampMs = stamp ? new Date(stamp).getTime() : NaN;
  const createdAt = listing?.attributes?.createdAt;
  const createdMs = createdAt ? new Date(createdAt).getTime() : NaN;
  const candidates = [stampMs, createdMs].filter(Number.isFinite);
  return candidates.length ? Math.max(...candidates) : null;
};

/** True when nothing says the listing was touched in the last 12 months.
 * A listing with no date at all is not called stale — nothing proves it. */
export const isStaleListing = (listing, now = Date.now()) => {
  const freshness = freshnessMsOf(listing);
  return freshness !== null && now - freshness > STALE_AFTER_MS;
};

/* ---- client-side sorts (BR-4) ---- */

const stableSortBy = (items, scoreOf) =>
  items
    .map((item, index) => ({ item, index, score: scoreOf(item) }))
    .sort((a, b) => (a.score === b.score ? a.index - b.index : a.score - b.score))
    .map(({ item }) => item);

/**
 * "Passar inte" never ranks above a fitting listing (BR-1): a stable sort on
 * the fit band, so whatever order the chosen sort made stays inside a band.
 */
export const rankFitFirst = (listings = [], car) =>
  !car || !Array.isArray(listings) ? listings : stableSortBy(listings, l => fitBandOf(l, car));

/* Lowest total for the set the card prices; listings without a price last. */
const setTotalOf = l => {
  const pieces = listingPricePieces(l);
  return pieces ? pieces.set : Number.MAX_SAFE_INTEGER;
};

/**
 * Reorders the loaded page for the sorts the Marketplace API cannot express.
 * Any other sort key returns the input untouched (the server already ordered
 * it).
 */
export const clientSortListings = (listings = [], sortKey, options = {}) => {
  if (!Array.isArray(listings) || listings.length === 0) return listings;

  if (sortKey === '-price') {
    /* "Lägsta pris för 4" (BR-1): the server sorted by raw price, which ranks
     * a whole-set price against per-piece prices. Re-rank the page by the
     * price for the set the card shows. */
    return stableSortBy(listings, setTotalOf);
  }

  if (sortKey === 'mountedTotal') {
    /* Mounting-capable listings by set total + montering, then the rest by
     * set total alone. */
    return stableSortBy(listings, l => {
      const mounted = mountedTotalAmount(l);
      if (mounted !== null) return mounted;
      const pieces = listingPricePieces(l);
      return 1e15 + (pieces ? pieces.set : Number.MAX_SAFE_INTEGER / 2);
    });
  }

  if (sortKey === 'bestMatch') {
    /* "Passar bäst" (BR-1) = fit, then in stock, then the lowest total for
     * the set. Only with a car — without one the server's order stands. */
    const car = options.car;
    if (!car) return listings;
    return stableSortBy(listings, l => {
      const inStock = stockQuantityOf(l) === null || stockQuantityOf(l) > 0;
      return fitBandOf(l, car) * 1e16 + (inStock ? 0 : 1e15) + setTotalOf(l);
    });
  }

  if (sortKey === 'fastestReady') {
    /* In stock at a seller who mounts beats in stock beats mounting offered
     * beats the rest. Within a band the server order (freshness/relevance)
     * stands. */
    return stableSortBy(listings, l => {
      const inStock = (stockQuantityOf(l) ?? 0) > 0;
      const mounts = !!monteringServiceFor(l?.author, l?.attributes?.publicData);
      if (inStock && mounts) return 0;
      if (inStock) return 1;
      if (mounts) return 2;
      return 3;
    });
  }

  return listings;
};
